import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { authApi } from "@/lib/apiClient";
import { OnboardingTour } from "@/components/onboarding/OnboardingTour";

const OnboardingContext = createContext(null);

const PUBLIC_PATHS = ["/", "/login", "/register"];

export const OnboardingProvider = ({ children }) => {
    const { user, patchUser } = useAuth();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const autoStarted = useRef(false);
    const lastUserId = useRef(null);

    // First-run: auto-open once for accounts that haven't seen the tour, but never
    // over the public marketing/auth pages. Reset when a different user signs in.
    useEffect(() => {
        const uid = user?.id || null;
        if (uid !== lastUserId.current) { lastUserId.current = uid; autoStarted.current = false; }
        if (user && user.onboarded === false && !autoStarted.current && !PUBLIC_PATHS.includes(location.pathname)) {
            autoStarted.current = true;
            setOpen(true);
        }
    }, [user, location.pathname]);

    const startTour = useCallback(() => setOpen(true), []);

    const complete = useCallback(async () => {
        setOpen(false);
        if (user && !user.onboarded) {
            patchUser({ onboarded: true });
            try { await authApi.setOnboarded(); } catch (e) { /* best-effort */ }
        }
    }, [user, patchUser]);

    return (
        <OnboardingContext.Provider value={{ startTour }}>
            {children}
            <OnboardingTour open={open} onClose={complete} />
        </OnboardingContext.Provider>
    );
};

export const useOnboarding = () => {
    const ctx = useContext(OnboardingContext);
    if (!ctx) throw new Error("useOnboarding must be used within OnboardingProvider");
    return ctx;
};
